import { useState, useCallback } from 'react'
import { Bell, ListCheck, MapPin, TriangleAlert } from 'lucide-react'
import {
  CryptoIcon,
  FilterStatus,
  RejectConfirmationDialog,
  ApproveMfaDialog,
  SwapArrow,
  cn,
  useInterval,
  getNetworkName,
  ellipsisWalletAddress,
} from '@valiant/ui-shared'
import { Button, Badge } from '@valiant/ui-shared'
import { toast } from 'sonner'
import {
  useGetSwapsQuery,
  useApproveSwapMutation,
  useRejectSwapMutation,
  useApproveWhitelistedAddressMutation,
  useRejectWhitelistedAddressMutation,
} from '../../../generated/graphql'
import { useNavigate } from 'react-router-dom'
import { useNavigation } from '../../../contexts/NavigationContext'
import { usePendingAddresses } from '../../../hooks/usePendingAddresses'
import { useAuth } from '../../../AuthProvider'
import { environment } from '../../../environment'

export function NotificationsContent({ className }: { className?: string }) {
  const { auth } = useAuth()
  const navigate = useNavigate()
  const { selectedGroupId } = useNavigation()

  // Modal states for swaps
  const [approveSwapDialogOpen, setApproveSwapDialogOpen] = useState(false)
  const [rejectSwapDialogOpen, setRejectSwapDialogOpen] = useState(false)
  const [selectedSwapId, setSelectedSwapId] = useState<string | null>(null)
  const [isApprovingSwap, setIsApprovingSwap] = useState(false)

  // Modal states for addresses
  const [approveAddressDialogOpen, setApproveAddressDialogOpen] = useState(false)
  const [rejectAddressDialogOpen, setRejectAddressDialogOpen] = useState(false)
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null)

  // Query for pending (requested) swaps only
  const [{ data: swapsData, fetching: swapsFetching }, refetchSwaps] = useGetSwapsQuery({
    variables: {
      filter: {
        //@ts-expect-error - This is a temporary fix to allow the status to be set to Awaiting
        status: FilterStatus.Awaiting,
        groupId: selectedGroupId,
      },
    },
    requestPolicy: 'network-only',
  })

  // Get pending addresses for the selected group
  const { pendingAddresses } = usePendingAddresses(selectedGroupId)

  // Auto-refetch pending swaps every 10 seconds for real-time updates
  useInterval(() => {
    if (selectedGroupId) {
      refetchSwaps({ requestPolicy: 'network-only' })
    }
  }, 10000)

  // Mutation hooks for swaps
  const [, executeApproveSwap] = useApproveSwapMutation()
  const [, executeRejectSwap] = useRejectSwapMutation()

  // Mutation hooks for addresses
  const [, executeApproveAddress] = useApproveWhitelistedAddressMutation()
  const [, executeRejectAddress] = useRejectWhitelistedAddressMutation()

  const pendingSwaps = swapsData?.getSwaps.nodes || []

  // Swap handlers
  const showApproveSwapDialog = (swapId: string) => {
    setSelectedSwapId(swapId)
    setApproveSwapDialogOpen(true)
  }

  const showRejectSwapDialog = (swapId: string) => {
    setSelectedSwapId(swapId)
    setRejectSwapDialogOpen(true)
  }

  const handleApproveSwapConfirm = useCallback(
    async (proof: { mfaCode?: string; passkeyApproval?: string }) => {
      if (!selectedSwapId) return

      setIsApprovingSwap(true)
      try {
        const result = await executeApproveSwap({
          input: { swapId: selectedSwapId, ...proof },
        })

        if (result.error) {
          const errorMessage = result.error.message
          if (errorMessage.includes('Invalid MFA code')) {
            toast.error('Invalid MFA code')
          } else {
            toast.error('Failed to approve swap: ' + errorMessage)
          }
          return
        }

        if (result.data?.approveSwap.id) {
          toast.success('Swap approved successfully')
          refetchSwaps({ requestPolicy: 'network-only' })
          setApproveSwapDialogOpen(false)
          setSelectedSwapId(null)
        } else {
          toast.error('Failed to approve swap')
        }
      } catch (error) {
        toast.error('An unexpected error occurred')
        console.error('Approve swap error:', error)
      } finally {
        setIsApprovingSwap(false)
      }
    },
    [selectedSwapId, executeApproveSwap, refetchSwaps],
  )

  const handleRejectSwapConfirm = async () => {
    if (!selectedSwapId) return

    try {
      const result = await executeRejectSwap({
        input: { swapId: selectedSwapId, reason: 'No reason provided' },
      })

      if (result.error) {
        toast.error('Failed to reject swap: ' + result.error.message)
        return
      }

      if (result.data?.rejectSwap.id) {
        toast.success('Swap rejected successfully')
        refetchSwaps({ requestPolicy: 'network-only' })
        setRejectSwapDialogOpen(false)
        setSelectedSwapId(null)
      } else {
        toast.error('Failed to reject swap')
      }
    } catch (error) {
      toast.error('An unexpected error occurred')
      console.error('Reject swap error:', error)
    }
  }

  // Address handlers
  const showApproveAddressDialog = (addressId: string) => {
    setSelectedAddressId(addressId)
    setApproveAddressDialogOpen(true)
  }

  const showRejectAddressDialog = (addressId: string) => {
    setSelectedAddressId(addressId)
    setRejectAddressDialogOpen(true)
  }

  const handleApproveAddressConfirm = async (proof: { mfaCode?: string; passkeyApproval?: string }) => {
    if (!selectedAddressId) return

    try {
      const result = await executeApproveAddress({
        input: { id: selectedAddressId, ...proof },
      })

      if (result.error) {
        const errorMessage = result.error.message
        if (errorMessage.includes('Invalid MFA code')) {
          toast.error('Invalid MFA code')
        } else {
          toast.error('Failed to approve address: ' + errorMessage)
        }
        return
      }

      if (result.data?.approveWhitelistedAddress.id) {
        toast.success('Address approved successfully')
        setApproveAddressDialogOpen(false)
        setSelectedAddressId(null)
      } else {
        toast.error('Failed to approve address')
      }
    } catch (error) {
      toast.error('An unexpected error occurred')
      console.error('Approve address error:', error)
    }
  }

  const handleRejectAddressConfirm = async () => {
    if (!selectedAddressId) return

    try {
      const result = await executeRejectAddress({
        input: { id: selectedAddressId },
      })

      if (result.error) {
        toast.error('Failed to reject address: ' + result.error.message)
        return
      }

      if (result.data?.rejectWhitelistedAddress.id) {
        toast.success('Address rejected successfully')
        setRejectAddressDialogOpen(false)
        setSelectedAddressId(null)
      } else {
        toast.error('Failed to reject address')
      }
    } catch (error) {
      toast.error('An unexpected error occurred')
      console.error('Reject address error:', error)
    }
  }

  const totalCount = pendingSwaps.length + pendingAddresses.length
  const fetching = swapsFetching

  return (
    <>
      <div className={cn('flex flex-col h-full p-5', className)}>
        <div className="mb-12.5">
          <div className="flex items-center gap-2 ">
            <Bell className="w-5 h-5" />
            <h3 className="font-semibold text-2xl">Notifications</h3>
          </div>

          {pendingSwaps.length > 0 && (
            <Badge
              variant="secondary"
              className="bg-destructive/10 text-destructive border border-destructive mt-4.5"
            >
              <TriangleAlert className="w-4 h-4" />
              All Estimated Rates
            </Badge>
          )}
        </div>

        <div className="h-full">
          {fetching ? (
            <div className="p-4">
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="flex justify-between items-start p-4 border-b border-gray-100">
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                        <div className="h-3 bg-gray-200 rounded w-32"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                        <div className="h-3 bg-gray-200 rounded w-12"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : totalCount > 0 ? (
            <div className="space-y-4 pb-4">
              {/* Pending Swaps Section */}
              {pendingSwaps.length > 0 && (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-500 uppercase tracking-wide">
                    <ListCheck className="w-4 h-4" />
                    <span>Pending Swaps ({pendingSwaps.length})</span>
                  </div>
                  {pendingSwaps.map((swap) => (
                    <div
                      key={swap.id}
                      className="p-6 border border-card-border rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col items-start mb-6 gap-2">
                        <div className="flex flex-col justify-center items-start w-full flex-wrap">
                          <h4 className="font-bold text-2xl text-wrap w-full leading-none -mt-1">
                            {swap.company}
                          </h4>
                          <p className="text-sm leading-none text-ellipsis overflow-hidden w-full mt-1">
                            {swap.requestedBy || 'Unknown User'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-center my-2 w-full">
                        <div className="flex items-center justify-between gap-2 text-sm text-gray-600 w-full">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CryptoIcon
                              symbol={swap.fromMint.symbol || ''}
                              imageOverride={swap.fromMint.image || ''}
                              className="w-8 h-8"
                            />
                            <div className="flex flex-col text-sm">
                              <span className="text-lg font-bold leading-none">
                                {swap.fromAmount}
                              </span>
                              <div className="flex flex-col">
                                <span className="text-sm font-bold">{swap.fromMint.symbol}</span>
                                <span className="text-xs text-card-text">
                                  {getNetworkName(
                                    swap.fromMint.chain || '',
                                    swap.fromMint.symbol || '',
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 md:mt-0">
                            <SwapArrow />
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            <CryptoIcon
                              symbol={swap.toMint.symbol || ''}
                              imageOverride={swap.toMint.image || ''}
                              className="w-8 h-8"
                            />
                            <div className="flex flex-col text-sm">
                              <span className="text-lg font-bold leading-none">
                                {swap.toAmount}
                              </span>
                              <div className="flex flex-col">
                                <span className="text-sm font-bold">{swap.toMint.symbol}</span>
                                <span className="text-xs text-card-text">
                                  {getNetworkName(
                                    swap.toMint.chain || '',
                                    swap.toMint.symbol || '',
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-6 mb-2.5">
                        <Button
                          variant="main_action"
                          className="flex-1 rounded-md"
                          onClick={() => showApproveSwapDialog(swap.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="main_outline"
                          className="flex-1 rounded-md"
                          onClick={() => showRejectSwapDialog(swap.id)}
                        >
                          Reject
                        </Button>
                      </div>

                      <Button
                        variant="main_outline"
                        className="w-full mt-2 rounded-md"
                        onClick={() => navigate(`/transactions/swap/${swap.id}`)}
                      >
                        View Details
                      </Button>
                    </div>
                  ))}
                </>
              )}

              {/* Pending Addresses Section */}
              {pendingAddresses.length > 0 && (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-500 uppercase tracking-wide mt-6">
                    <MapPin className="w-4 h-4" />
                    <span>Pending Addresses ({pendingAddresses.length})</span>
                  </div>
                  {pendingAddresses.map((address) => (
                    <div
                      key={address.id}
                      className="p-6 border border-card-border rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col items-start mb-4 gap-2">
                        <div className="flex flex-col justify-center items-start w-full flex-wrap">
                          <h4 className="font-bold text-xl text-wrap w-full leading-none -mt-1">
                            {address.companyName}
                          </h4>
                          <p className="text-sm leading-none text-ellipsis overflow-hidden w-full mt-1">
                            {address.requester?.username ||
                              address.requester?.email ||
                              'Unknown User'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-4">
                        <CryptoIcon symbol={address.chain} className="w-8 h-8" />
                        <div className="flex flex-col min-w-0 flex-1">
                          {address.label && (
                            <span className="text-sm font-bold">{address.label}</span>
                          )}
                          <span className="text-xs text-card-text font-mono">
                            {ellipsisWalletAddress(address.address, 25)}
                          </span>
                          <span className="text-xs text-gray-400">{address.chain}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="main_action"
                          className="flex-1 rounded-md"
                          onClick={() => showApproveAddressDialog(address.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="main_outline"
                          className="flex-1 rounded-md"
                          onClick={() => showRejectAddressDialog(address.id)}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-lg font-medium mb-1">All caught up!</p>
              <p className="text-sm">No pending notifications to review.</p>
            </div>
          )}
        </div>
      </div>

      {/* Swap Dialogs */}
      <ApproveMfaDialog
        open={approveSwapDialogOpen}
        title="Approve Swap"
        onOpenChange={setApproveSwapDialogOpen}
        onConfirm={handleApproveSwapConfirm}
        isSubmitting={isApprovingSwap}
        apiUrl={environment.API_URL}
        token={auth?.token}
        action="approveSwap"
        targetKey={selectedSwapId ?? undefined}
      />
      <RejectConfirmationDialog
        open={rejectSwapDialogOpen}
        title="Reject Swap"
        description="Are you sure you want to reject this swap? This action cannot be undone."
        onOpenChange={setRejectSwapDialogOpen}
        onConfirm={handleRejectSwapConfirm}
      />

      {/* Address Dialogs */}
      <ApproveMfaDialog
        open={approveAddressDialogOpen}
        title="Approve Address"
        onOpenChange={setApproveAddressDialogOpen}
        onConfirm={handleApproveAddressConfirm}
        apiUrl={environment.API_URL}
        token={auth?.token}
        action="approveWhitelistedAddress"
        targetKey={selectedAddressId ?? undefined}
      />
      <RejectConfirmationDialog
        open={rejectAddressDialogOpen}
        title="Reject Address"
        description="Are you sure you want to reject this whitelisted address? This action cannot be undone."
        onOpenChange={setRejectAddressDialogOpen}
        onConfirm={handleRejectAddressConfirm}
      />
    </>
  )
}
